import { Controller } from 'react-hook-form'
import type { UseFormReturn, UseFieldArrayReturn } from 'react-hook-form'
import { ErrorMessage, Field, FormLabel, Heading, Input, Select } from '@quickmd-llc/design-system'
import type { LicenseFormValues, LicenseEntryType } from '@/pages/member/license-form/LicenseFormSchema'
import { statesOptions } from '@/utils/common'

const ADDABLE_LICENSE_TYPES: LicenseEntryType[] = ['CSL', 'DEA']

const STATE_SELECT_OPTIONS = statesOptions.map((s) => {
  const code = s.slice(-2)
  return { label: s, value: code }
})

const LICENSE_TYPE_OPTIONS = [
  { label: 'Medical', value: 'Medical' },
  { label: 'CSL', value: 'CSL' },
  { label: 'DEA', value: 'DEA' },
]

const MAX_LICENSE_ENTRIES = 3

type Props = {
  licenseForm: UseFormReturn<LicenseFormValues>
  licenseFields: UseFieldArrayReturn<LicenseFormValues, 'licenseEntries'>['fields']
  appendEntry: UseFieldArrayReturn<LicenseFormValues, 'licenseEntries'>['append']
  removeEntry: UseFieldArrayReturn<LicenseFormValues, 'licenseEntries'>['remove']
  watchedEntries: LicenseFormValues['licenseEntries']
}

/**
 * "License information" section (Provider only): the state-license selector plus the
 * `useFieldArray`-driven license entries (Medical / CSL / DEA). The RHF block is kept intact —
 * the orchestrator owns the `useForm`/`useFieldArray` instances and passes them in.
 *
 * Already-Tailwind markup is carried over verbatim (this section had no `nm-*` SCSS classes).
 */
export const LicenseInformationSection = ({
  licenseForm,
  licenseFields,
  appendEntry,
  removeEntry,
  watchedEntries,
}: Props) => (
  <div className='rounded-lg border border-border-medium bg-white'>
    <div className='border-b border-border-medium px-sp-3 py-sp-2'>
      <Heading level='h3'>License information</Heading>
    </div>
    <div className='flex flex-col gap-sp-3 p-sp-3'>
      {/* State license — half width */}
      <div className='w-1/2 pr-sp-1.5'>
        <Field error={!!licenseForm.formState.errors.stateCode}>
          <FormLabel>State license</FormLabel>
          {licenseForm.formState.errors.stateCode ?
            <ErrorMessage>{licenseForm.formState.errors.stateCode.message}</ErrorMessage>
          : null}
          <Select
            {...licenseForm.register('stateCode', {
              onChange: () => licenseForm.clearErrors('stateCode'),
            })}
            placeholder='Select a state'
            error={!!licenseForm.formState.errors.stateCode}>
            {STATE_SELECT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {/* License entries */}
      <div className='flex flex-col gap-sp-2'>
        {licenseFields.map((field, idx) => {
          const entryErrors = licenseForm.formState.errors.licenseEntries?.[idx]
          const isFirstMedical = idx === 0 && watchedEntries[0]?.type === 'Medical'
          const currentType = watchedEntries[idx]?.type
          const usedTypes = watchedEntries.map((e) => e.type)

          return (
            <div key={field.id} className='flex items-end gap-sp-2'>
              {/* License type dropdown */}
              <Field className='flex-1'>
                <FormLabel>License type</FormLabel>
                <Select
                  {...licenseForm.register(`licenseEntries.${idx}.type`)}
                  disabled={isFirstMedical}
                  placeholder='Select an option'>
                  {LICENSE_TYPE_OPTIONS.map((opt) => {
                    const taken = usedTypes.includes(opt.value) && opt.value !== currentType
                    return (
                      <option key={opt.value} value={opt.value} disabled={taken}>
                        {opt.label}
                      </option>
                    )
                  })}
                </Select>
              </Field>

              {/* License number input */}
              <Field error={!!entryErrors?.number} className='flex-1'>
                <FormLabel>License no.</FormLabel>
                {entryErrors?.number ?
                  <ErrorMessage>{entryErrors.number.message}</ErrorMessage>
                : null}
                <Controller
                  control={licenseForm.control}
                  name={`licenseEntries.${idx}.number`}
                  render={({ field: f }) => (
                    <Input
                      {...f}
                      value={f.value ?? ''}
                      onChange={(e) => {
                        f.onChange(e.target.value.toUpperCase())
                        licenseForm.clearErrors(`licenseEntries.${idx}.number`)
                      }}
                      placeholder='Enter license number'
                      error={!!entryErrors?.number}
                    />
                  )}
                />
              </Field>

              {/* DEA state dropdown — only for DEA type */}
              {currentType === 'DEA' && (
                <Field error={!!entryErrors?.deaStateCode} className='flex-1'>
                  <FormLabel>DEA state</FormLabel>
                  {entryErrors?.deaStateCode ?
                    <ErrorMessage>{entryErrors.deaStateCode.message}</ErrorMessage>
                  : null}
                  <Select
                    {...licenseForm.register(`licenseEntries.${idx}.deaStateCode`)}
                    placeholder='Select state'
                    error={!!entryErrors?.deaStateCode}>
                    {STATE_SELECT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}

              {/* Trash icon — not shown for first Medical row */}
              {!isFirstMedical ?
                <button
                  type='button'
                  onClick={() => removeEntry(idx)}
                  aria-label='Remove license entry'
                  className='flex size-[44px] shrink-0 items-center justify-center text-text-light hover:text-text-error'
                  data-testid='pw_nm-remove-license'>
                  <svg width='18' height='18' viewBox='0 0 448 512' fill='currentColor'>
                    <path d='M135.2 17.7C140.6 6.8 151.7 0 163.8 0h120.4c12.1 0 23.2 6.8 28.6 17.7L320 32h80c17.7 0 32 14.3 32 32s-14.3 32-32 32H48C30.3 96 16 81.7 16 64s14.3-32 32-32h80l7.2-14.3zM32 128h384v320c0 35.3-28.7 64-64 64H96c-35.3 0-64-28.7-64-64V128zm96 64c-8.8 0-16 7.2-16 16v224c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16v224c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16v224c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16z' />
                  </svg>
                </button>
              : <div className='size-[44px] shrink-0' />}
            </div>
          )
        })}

        {/* Add button — hidden when max rows reached */}
        {licenseFields.length < MAX_LICENSE_ENTRIES && (
          <button
            type='button'
            onClick={() => {
              const usedTypes = watchedEntries.map((e) => e.type)
              const nextType = ADDABLE_LICENSE_TYPES.find((t) => !usedTypes.includes(t)) ?? 'CSL'
              appendEntry({ type: nextType, number: '', deaStateCode: '' })
            }}
            className='mt-sp-1 flex w-fit items-center gap-sp-0.5 text-sm font-semibold text-[#178199]'
            data-testid='pw_nm-add-license'>
            <svg width='14' height='14' viewBox='0 0 14 14' fill='currentColor'>
              <path d='M7 1a.75.75 0 0 1 .75.75v4.5h4.5a.75.75 0 0 1 0 1.5h-4.5v4.5a.75.75 0 0 1-1.5 0v-4.5h-4.5a.75.75 0 0 1 0-1.5h4.5v-4.5A.75.75 0 0 1 7 1z' />
            </svg>
            Add
          </button>
        )}
      </div>
    </div>
  </div>
)
